import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Building2, Plus, Search, Unlink, Lock } from 'lucide-react';
import platformAdminService from '../../services/platformAdminService';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { describeApiError, mapApiError } from '../../utils/apiError';
import { isHomeCompany } from '../../utils/tenancy';
import { PLATFORM_PARTNER_COPY } from '../../constants/partnerCopy';
import PaginationBar from './PaginationBar';
import styles from './PlatformAdmin.module.scss';

const PAGE_SIZE = 50;

// §4.5 error table.
const ATTACH_ERRORS = [
  { status: 400, match: /belongs to another partner/i, message: 'The company belongs to another partner; detach it first' },
  { status: 404, match: /not found/i, message: 'That company is no longer under this partner.' },
];

/**
 * A partner's client companies (Partner tier guide §4.5). Attaching moves an
 * existing platform-direct company under the partner; detaching makes it
 * platform-direct again. The home company never appears here.
 */
const PartnerCompaniesTab = ({ partner, partnerIndex, notify, onChanged }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const [attachOpen, setAttachOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [attachId, setAttachId] = useState('');
  const [detachTarget, setDetachTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await platformAdminService.listPartnerCompanies(partner.id, {
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      const list = data?.companies || (Array.isArray(data) ? data : []);
      setCompanies(list);
      setTotal(Number.isFinite(data?.total) ? data.total : list.length);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load companies.'));
    } finally {
      setLoading(false);
    }
  }, [partner.id, debouncedSearch, offset]);

  useEffect(() => {
    load();
  }, [load]);

  // Only platform-direct companies can be attached; never a home company.
  const openAttach = async () => {
    setAttachOpen(true);
    setAttachId('');
    try {
      setCandidatesLoading(true);
      const data = await platformAdminService.listCompanies(false, { limit: 200 });
      const list = data?.companies || (Array.isArray(data) ? data : []);
      setCandidates(
        list.filter((c) => c.partner_id == null && !isHomeCompany(c.id, { partnerIndex, name: c.name }))
      );
    } catch (err) {
      notify(describeApiError(err, 'Failed to load companies.'), 'error');
    } finally {
      setCandidatesLoading(false);
    }
  };

  const attachCompany = useMemo(
    () => candidates.find((c) => c.id === attachId) || null,
    [candidates, attachId]
  );

  const handleAttach = async () => {
    if (!attachCompany) return;
    try {
      setBusy(true);
      await platformAdminService.attachPartnerCompany(partner.id, attachCompany.id);
      notify(`${attachCompany.name} added to ${partner.name}.`, 'success');
      setAttachOpen(false);
      await load();
      await onChanged?.();
    } catch (err) {
      notify(mapApiError(err, ATTACH_ERRORS, 'Failed to add the company.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDetach = async () => {
    if (!detachTarget) return;
    try {
      setBusy(true);
      await platformAdminService.detachPartnerCompany(partner.id, detachTarget.id);
      notify(`${detachTarget.name} is now platform-direct.`, 'info');
      setDetachTarget(null);
      await load();
      await onChanged?.();
    } catch (err) {
      notify(mapApiError(err, ATTACH_ERRORS, 'Failed to detach the company.'), 'error');
      if (err?.status === 404) {
        setDetachTarget(null);
        await load();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box className={styles.toolbar}>
        <TextField
          size="small"
          placeholder="Search companies…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" startIcon={<Plus size={18} />} className={styles.primaryBtn} onClick={openAttach}>
          Add existing company
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : companies.length === 0 ? (
        <Box className={styles.emptyState}>
          <Building2 className={styles.emptyIcon} size={56} />
          <Typography variant="h6">No client companies</Typography>
          <Typography variant="body2">
            The partner can create its own companies, or you can move an existing company under it.
          </Typography>
        </Box>
      ) : (
        <Box className={styles.tableCard}>
          {companies.map((company) => (
            <Box key={company.id} className={styles.userRow}>
              <Box className={styles.companyMain}>
                <span className={styles.companyAvatar}>{(company.name || '?').charAt(0).toUpperCase()}</span>
                <Box>
                  <span className={styles.companyName}>{company.name}</span>
                  <span className={styles.companyMeta}>ID {company.id}</span>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <span className={`${styles.statusPill} ${company.is_active ? styles.active : styles.inactive}`}>
                  {company.is_active ? 'Active' : 'Inactive'}
                </span>
                <Button
                  size="small"
                  className={styles.subtleBtn}
                  startIcon={<Unlink size={14} />}
                  onClick={() => setDetachTarget(company)}
                >
                  Detach
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {total > 0 && (
        <PaginationBar total={total} limit={PAGE_SIZE} offset={offset} loading={loading} unit="companies" onOffsetChange={setOffset} />
      )}

      {/* Attach */}
      <Dialog open={attachOpen} onClose={() => !busy && setAttachOpen(false)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Add a company to {partner.name}</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {candidatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress color="primary" size={28} />
            </Box>
          ) : candidates.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Every company already belongs to a partner.
            </Typography>
          ) : (
            <FormControl fullWidth>
              <InputLabel>Platform-direct company</InputLabel>
              <Select label="Platform-direct company" value={attachId} onChange={(e) => setAttachId(e.target.value)}>
                {candidates.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {attachCompany && (
            <Box className={styles.noteBanner}>
              <span>{PLATFORM_PARTNER_COPY.attachBody(attachCompany.name, partner.name)}</span>
            </Box>
          )}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setAttachOpen(false)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleAttach} disabled={busy || !attachCompany} className={styles.primaryBtn}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Add to partner'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detach */}
      <Dialog open={!!detachTarget} onClose={() => !busy && setDetachTarget(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Detach company</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><Lock /></Box>
          <Typography className={styles.deleteText}>
            {detachTarget && PLATFORM_PARTNER_COPY.detachBody(detachTarget.name, partner.name)}
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setDetachTarget(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDetach}
            disabled={busy}
            className={styles.dangerButton}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Detach
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PartnerCompaniesTab;
